import React, { useEffect, useState } from 'react';
import styles from './Login.module.css';
import { useForm, SubmitHandler } from 'react-hook-form';
import { ReactComponent as Logo } from 'assets/images/ksquare-logo.svg';
import SalesforceLogo from 'assets/images/salesforce-logo.png';
import { LoginUserPayload } from 'features/auth/authActions';
import { useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import { RootState } from 'app/store';
import { useAppDispatch } from 'app/hooks';
import { Box, Grid, IconButton, InputAdornment, TextField, Typography } from '@mui/material';

import { VisibilityOff, Visibility } from '@mui/icons-material';
import { setCredentials } from 'features/auth/authSlice';
import { useLoginMutation } from 'features/auth/authApi';

function Login() {
  const [login] = useLoginMutation();
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [isSandboxChecked, setIsSandboxChecked] = useState(false);
  const ksBranding = process.env.REACT_APP_BRANDING == 'yes' ? true : false;

  const togglePasswordVisibility = () => {
    setPasswordVisible(!passwordVisible);
  };

  const dispatch = useAppDispatch();

  const { accessToken } = useSelector((state: RootState) => state.auth);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginUserPayload>();
  const navigate = useNavigate();

  useEffect(() => {
    if (accessToken) navigate('/');
  }, [navigate, accessToken]);

  const onSubmit: SubmitHandler<LoginUserPayload> = async data => {
    try {
      const userData = await login(data).unwrap();
      dispatch(setCredentials({ ...userData }));
      navigate('/');
    } catch (err) {
      console.error('err: ', err);
    }
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setIsSandboxChecked(event.target.checked);
  };

  const LoginSalesforce = () => {
    const redirectUrl = `${process.env.REACT_APP_BASE_URL_DEV}/salesforceauth/login/?setToSandbox=${isSandboxChecked}`;
    window.location.href = redirectUrl;
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Grid
        container
        className={ksBranding ? styles.background : styles.backgroundnobranding}
        justifyContent={'flex-end'}
        alignItems='center'
        direction='row'
      >
        <Grid container direction={'column'} alignItems='end' justifyContent='center' item xs={12}>
          <Box className={styles.outterContainer} display='flex' flexDirection='column' width='100%'>
            <Typography className={styles.title} mb={'32px'}>
              WELCOME TO DOCGENIUS
            </Typography>
            <Grid
              container
              item
              className={styles.loginContainer}
              direction='column'
              alignItems='center'
              alignContent='center'
              justifyContent='space-between'
            >
              <Grid item>{ksBranding && <Logo />}</Grid>
              <Grid item className={styles.gridContainer}>
                <TextField
                  required
                  fullWidth
                  id='outlined-required'
                  label='Email'
                  placeholder='Email'
                  {...register('email', { required: 'Email is required' })}
                />
                {errors.email && <p>{errors.email.message as string}</p>}
              </Grid>
              <Grid item className={styles.gridContainer}>
                <TextField
                  required
                  fullWidth
                  id='outlined-required'
                  label='Password'
                  type={passwordVisible ? 'text' : 'password'}
                  placeholder='Password'
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position='end'>
                        <IconButton
                          aria-label='toggle password visibility'
                          onClick={togglePasswordVisibility}
                          edge='end'
                        >
                          {passwordVisible ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  {...register('password', { required: 'Password is required' })}
                />
                {errors.password && <p>{errors.password.message as string}</p>}
              </Grid>
              <Grid container justifyContent='space-between'>
                <Grid item>
                  <label className={styles.checkboxLabel}>
                    <input type='checkbox' className={styles.checkbox} />
                    <span className={styles.labelText}>Remember Me</span>
                  </label>
                </Grid>
                <Grid item>
                  <span className={styles.forgotPassword}>Forgot Password?</span>
                </Grid>
              </Grid>
              <Grid item className={styles.gridContainer}>
                <button type='submit' className={styles.loginButton}>
                  Log in
                </button>
              </Grid>
              <Grid item className={styles.gridContainer}>
                <div className={styles.accountText}>Or join with your existing account</div>
              </Grid>
              <Grid item className={styles.salesforceLoginButton} onClick={LoginSalesforce}>
                <img src={SalesforceLogo} className={styles.salesforceLogo} alt='Saleforce logo' />
                <span className={styles.salesforcetLoginText}>Sign in with Salesforce</span>
              </Grid>
              <Grid item>
                <label className={styles.checkboxLabel}>
                  <input
                    type='checkbox'
                    className={styles.checkbox}
                    checked={isSandboxChecked}
                    onChange={handleCheckboxChange}
                  />
                  <span className={styles.sandBoxTitle}>Sign in to the Sandbox</span>
                </label>
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </Grid>
    </form>
  );
}

export default Login;
